import { type JSX, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Icon } from '@/components/icons';
import {
  Donut,
  DonutLegend,
  HeroFigure,
  HeroStat,
  KpiChip,
  ProgressRing,
  SectionTitle,
  StatusStackedBar,
  fmtPct,
  formatRate,
  pctInt,
  type ReportBucket,
} from '@/components/report';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  Skeleton,
  Spinner,
  StatusPill,
} from '@/components/ui';
import {
  downloadCampaignCSV,
  useCampaign,
  useCampaignErrors,
  useCampaignMessages,
  useCampaignThroughput,
  useCancelCampaign,
  useDuplicateCampaign,
  type CampaignItem,
  type CampaignMessage,
  type ThroughputPoint,
  type TopError,
} from '@/hooks/useCampaigns';
import { messageDisplayStatus } from '@/hooks/useMessages';
import { useSendersList } from '@/hooks/useSenders';
import { ApiError } from '@/lib/api';
import { formatCurrency, formatRelativeTime } from '@/lib/format';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function CampaignDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = idParam ? Number(idParam) : NaN;
  const validId = Number.isFinite(id);

  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const campaign = useCampaign(validId ? id : null);
  const throughput = useCampaignThroughput(campaign.data ?? null, 60);
  const errors = useCampaignErrors(validId ? id : null, 5);
  const senders = useSendersList();
  const messages = useCampaignMessages(validId ? id : null);
  const cancel = useCancelCampaign();
  const duplicate = useDuplicateCampaign();

  const [confirmStop, setConfirmStop] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleStop = async () => {
    if (!validId) return;
    setActionError(null);
    try {
      await cancel.mutateAsync(id);
      setConfirmStop(false);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : t('cd_action_err'));
    }
  };

  const handleDuplicate = async () => {
    if (!validId) return;
    setActionError(null);
    try {
      const draft = await duplicate.mutateAsync(id);
      navigate(`/campaigns/${draft.id}/edit`);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : t('cd_action_err'));
    }
  };

  const handleExport = async () => {
    if (!validId) return;
    setActionError(null);
    setExporting(true);
    try {
      await downloadCampaignCSV(id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('cd_action_err'));
    } finally {
      setExporting(false);
    }
  };

  // Resolve the sender display value once, so the meta row shows the same
  // string the operator picked in the wizard rather than the raw FK.
  const senderLabel = useMemo(() => {
    const c = campaign.data;
    if (!c) return '';
    const sender = senders.data?.items.find((s) => s.id === c.sender_id);
    return sender ? sender.value : `#${c.sender_id}`;
  }, [campaign.data, senders.data]);

  if (!validId) {
    return (
      <div className="px-8 py-8 max-w-[1100px] mx-auto">
        <p className="text-[13px] text-danger">{t('cd_invalid_id')}</p>
        <Button variant="secondary" className="mt-3" onClick={() => navigate('/campaigns')}>
          {t('cd_back_link')}
        </Button>
      </div>
    );
  }

  const c = campaign.data;
  const isLive = c?.status === 'running' || c?.status === 'queued';

  return (
    <div className="px-8 py-8 max-w-[1100px] mx-auto">
      {/* Breadcrumb back to list */}
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-1 text-[12px] text-text-3 hover:text-text mb-3"
      >
        <Icon.chevronLeft width="14" height="14" />
        {t('cd_back_link')}
      </Link>

      {/* Page header */}
      {!c ? (
        <Skeleton className="h-10 w-72 mb-2" />
      ) : (
        <header className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="display text-[26px] font-semibold tracking-tight text-text leading-none m-0">
                {c.name}
              </h1>
              <StatusPill status={c.status} />
            </div>
            <div className="flex items-center gap-2 flex-wrap text-[12px] text-text-3 mt-2">
              <span>{t(`campaign_type_${c.message_type}`, { defaultValue: c.message_type })}</span>
              <span>·</span>
              <span className="mono">{senderLabel}</span>
              <span>·</span>
              <span>{t('cd_meta_recipients', { total: c.total_recipients.toLocaleString(lang) })}</span>
              {c.started_at && (
                <>
                  <span>·</span>
                  <span title={new Date(c.started_at).toLocaleString(lang)}>
                    {t('cd_meta_started', { when: formatRelativeTime(c.started_at, lang) })}
                  </span>
                </>
              )}
              <span>·</span>
              <span className="mono text-text-4">{c.uuid.slice(0, 8)}</span>
            </div>
          </div>

          {/* Action buttons. Stop only when in flight; Duplicate + Export
              are useful from any state. Stop opens a confirm dialog
              because cancellation is destructive (in-flight messages
              already handed to the carrier still deliver). */}
          <div className="flex gap-2 flex-wrap">
            {isLive && (
              <Button
                variant="secondary"
                onClick={() => setConfirmStop(true)}
                disabled={cancel.isPending}
                className="text-warn hover:bg-warn/10"
              >
                {t('cd_action_stop')}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => void handleDuplicate()}
              disabled={duplicate.isPending}
            >
              {duplicate.isPending ? <Spinner /> : <Icon.plus width="14" height="14" />}
              {t('cd_action_duplicate')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void handleExport()}
              disabled={exporting}
            >
              {exporting ? t('cd_action_exporting') : t('cd_action_export')}
            </Button>
          </div>
        </header>
      )}

      {actionError && (
        <div
          role="alert"
          className="px-3.5 py-3 mt-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger"
        >
          {actionError}
        </div>
      )}

      {/* Report band — mirrors the voice campaign report: completion ring +
          delivery hero stats, a stacked status breakdown, an icon KPI strip,
          and an outcome-mix donut. "Pending" (sent-but-no-DLR-yet) is shown
          as a neutral info bucket inside the status bar / donut, never framed
          as a failure KPI — to a customer a scary "pending" count reads as
          "not sent" when those messages were in fact handed to the carrier. */}
      {!c ? (
        <div className="space-y-4 mt-5 mb-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Skeleton className="h-44 w-full" />
            <Skeleton className="h-44 w-full" />
          </div>
          <Skeleton className="h-40 w-full" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[76px] w-full" />
            ))}
          </div>
        </div>
      ) : (
        <ReportBand c={c} lang={lang} />
      )}

      {/* Throughput chart */}
      <Card padding="lg" className="mb-5 min-h-[300px]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="display text-[15px] font-semibold text-text">{t('cd_throughput_title')}</h2>
          <div className="flex gap-3 text-[12px] text-text-3">
            <LegendDot color="var(--volt-500)" label={t('cd_legend_sent')} />
            <LegendDot color="var(--success)" label={t('cd_legend_delivered')} />
            <LegendDot color="var(--danger)" label={t('cd_legend_failed')} />
          </div>
        </div>
        {throughput.isLoading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : (throughput.data?.items.length ?? 0) === 0 ? (
          <div className="grid place-items-center h-[240px] text-[13px] text-text-3">
            {t('cd_no_throughput')}
          </div>
        ) : (
          <ThroughputChart points={throughput.data?.items ?? []} lang={lang} />
        )}
      </Card>

      {/* Top errors + Message template — two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card padding="none" className="lg:col-span-3">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="display text-[15px] font-semibold text-text">{t('cd_template_title')}</h2>
            <p className="text-[12px] text-text-3 mt-0.5">{t('cd_template_sub')}</p>
          </div>
          <div className="p-5">
            {!c ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <pre className="whitespace-pre-wrap text-[13px] text-text leading-relaxed bg-surface-2 border border-line rounded-md p-4 mono">
                {c.message_template}
              </pre>
            )}
          </div>
        </Card>

        <Card padding="none" className="lg:col-span-2">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="display text-[15px] font-semibold text-text">{t('cd_errors_title')}</h2>
          </div>
          {errors.isLoading ? (
            <div className="p-5 space-y-2">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (errors.data?.items.length ?? 0) === 0 ? (
            <div className="p-8 text-center text-[13px] text-text-3">{t('cd_no_errors')}</div>
          ) : (
            <ul className="divide-y divide-line">
              {errors.data?.items.map((e) => (
                <ErrorRow key={e.error_code} e={e} lang={lang} />
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Per-message table — caps at the first 200 messages (the
          /v1/campaigns/{id}/messages endpoint paginates but the UI
          shows the most recent batch; for the full set, Export CSV. */}
      <Card padding="none" className="mt-5">
        <div className="px-5 py-4 border-b border-line flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="display text-[15px] font-semibold text-text">
              {t('cd_messages_title')}
            </h2>
            <p className="text-[12px] text-text-3 mt-0.5">{t('cd_messages_sub')}</p>
          </div>
          <Button variant="secondary" onClick={() => void handleExport()} disabled={exporting}>
            {exporting ? t('cd_action_exporting') : t('cd_action_export_csv')}
          </Button>
        </div>
        <MessagesTable
          messages={messages.data?.items ?? []}
          loading={messages.isLoading}
          lang={lang}
        />
      </Card>

      {/* Spend footer line */}
      {c && (
        <div className="text-[12px] text-text-3 text-right mt-4 num">
          {t('cd_spend')}: {formatCurrency(c.delivered_count * 0.0192 + c.sent_count * 0.0192, lang)}
          <span className="text-text-4 ml-1">({t('cd_spend_estimate')})</span>
        </div>
      )}

      <ConfirmModal
        open={confirmStop}
        title={t('cd_stop_title')}
        description={c ? t('cd_stop_desc', { name: c.name }) : ''}
        confirmLabel={t('cd_action_stop')}
        cancelLabel={t('cd_stop_cancel')}
        tone="danger"
        busy={cancel.isPending}
        onConfirm={() => void handleStop()}
        onClose={() => setConfirmStop(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * ReportBand — the SMS campaign report hero, mirroring the voice campaign
 * report layout with the shared `components/report` primitives:
 *   1. Hero band: completion ring (sent / total) + delivery-rate and delivered
 *      hero stats.
 *   2. Status detail: a stacked bar over delivered / pending / failed / to-send
 *      that reconciles to the recipient total.
 *   3. KPI strip: sent / delivered / failed / delivery rate icon chips.
 *   4. Outcome-mix donut: delivered / pending / failed, centered on the
 *      delivery rate.
 *
 * Derived counters (all clamped to >= 0 so a mid-flight snapshot never goes
 * negative):
 *   - toSend  = total_recipients - sent_count      (queued, not yet dispatched)
 *   - pending = sent_count - delivered - failed     (dispatched, awaiting DLR)
 *   - delivery rate = delivered / sent              (provider-aligned; pending
 *     stays in the denominator, matching the Infobip dashboard).
 */
function ReportBand({ c, lang }: { c: CampaignItem; lang: string }): JSX.Element {
  const { t } = useTranslation('default');

  // Coarse-bucket throughput series feeding the "outcome by date" panel. This
  // is a SECOND throughput query, separate from the 60s series that feeds the
  // throughput line chart lower down. Bucket granularity tracks the campaign
  // span so the bar count stays readable: hourly (3600s) for runs under two
  // days, daily (86400s) once the campaign spans longer. The threshold makes
  // the derived bucket stable across renders, so the React Query key doesn't
  // churn.
  const spanMs = Date.now() - new Date(c.started_at ?? c.created_at).getTime();
  const outcomeBucketSecs = spanMs < 2 * 24 * 60 * 60 * 1000 ? 3600 : 86400;
  const outcomeSeries = useCampaignThroughput(c, outcomeBucketSecs);

  const total = c.total_recipients;
  const sent = c.sent_count;
  const delivered = c.delivered_count;
  const failed = c.failed_count;
  const toSend = Math.max(total - sent, 0);
  const pending = Math.max(sent - delivered - failed, 0);
  const deliveryRate = sent > 0 ? delivered / sent : 0;
  const ringPct = total > 0 ? (sent / total) * 100 : 0;
  const pctOfSent = sent > 0 ? (delivered / sent) * 100 : 0;

  const statusBuckets: ReportBucket[] = [
    { key: 'delivered', label: t('cd_kpi_delivered'), count: delivered, sem: 'success' },
    { key: 'pending', label: t('cd_kpi_pending'), count: pending, sem: 'info' },
    { key: 'failed', label: t('cd_kpi_failed'), count: failed, sem: 'danger' },
    { key: 'tosend', label: t('cd_rep_hero_tosend'), count: toSend, sem: 'muted' },
  ];

  const outcomeSegments: ReportBucket[] = [
    { key: 'delivered', label: t('cd_kpi_delivered'), count: delivered, sem: 'success' },
    { key: 'pending', label: t('cd_kpi_pending'), count: pending, sem: 'info' },
    { key: 'failed', label: t('cd_kpi_failed'), count: failed, sem: 'danger' },
  ];
  const outcomeTotal = delivered + pending + failed;

  return (
    <div className="space-y-4 mt-5 mb-5">
      {/* Hero band */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <Card className="p-5">
          <div className="flex items-center gap-5">
            <ProgressRing pct={ringPct} centerLabel={t('cd_rep_ring_center')} />
            <div className="min-w-0 space-y-2.5 flex-1">
              <HeroFigure
                label={t('cd_rep_hero_list')}
                value={total.toLocaleString(lang)}
                sem="muted"
              />
              <HeroFigure
                label={t('cd_rep_hero_tosend')}
                value={toSend.toLocaleString(lang)}
                sem="info"
              />
              <HeroFigure
                label={t('cd_kpi_sent')}
                value={sent.toLocaleString(lang)}
                sem="success"
              />
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <HeroStat
            sem="success"
            icon={<Icon.campaigns width={17} height={17} />}
            label={t('cd_kpi_rate')}
            value={sent > 0 ? formatRate(deliveryRate) : '—'}
            pct={deliveryRate * 100}
            sub={t('cd_rep_delivered_of_sent', {
              delivered: delivered.toLocaleString(lang),
              sent: sent.toLocaleString(lang),
            })}
          />
          <HeroStat
            sem="info"
            icon={<Icon.chat width={17} height={17} />}
            label={t('cd_kpi_delivered')}
            value={delivered.toLocaleString(lang)}
            sub={t('cd_rep_pct_of_sent', { pct: fmtPct(pctOfSent) })}
          />
        </div>
      </div>

      {/* Status detail */}
      <Card className="p-5">
        <SectionTitle icon="chart">{t('cd_rep_section_status')}</SectionTitle>
        <StatusStackedBar
          buckets={statusBuckets}
          total={total}
          reconcileLabel={t('cd_rep_reconciles', { total: total.toLocaleString(lang) })}
        />
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiChip sem="info" icon="chat" label={t('cd_kpi_sent')} value={sent.toLocaleString(lang)} />
        <KpiChip
          sem="success"
          icon="campaigns"
          label={t('cd_kpi_delivered')}
          value={delivered.toLocaleString(lang)}
        />
        <KpiChip
          sem="danger"
          icon="xMark"
          label={t('cd_kpi_failed')}
          value={failed.toLocaleString(lang)}
        />
        <KpiChip
          sem="success"
          icon="chart"
          label={t('cd_kpi_rate')}
          value={sent > 0 ? formatRate(deliveryRate) : '—'}
        />
      </div>

      {/* Outcome mix — donut (composition) + by-date stacked bars (over time),
          side by side like the voice report's donut+funnel row. Both use the
          same semantic colors: delivered = success, pending = info,
          failed = danger. They answer different questions (what's the split /
          how did the split move over time) so they sit together. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Left: outcome-mix donut */}
        <Card className="p-5">
          <SectionTitle>{t('cd_rep_section_outcome')}</SectionTitle>
          {outcomeTotal === 0 ? (
            <p className="text-[12px] text-text-4">{t('cd_rep_no_outcome')}</p>
          ) : (
            <div className="flex items-center gap-5">
              <Donut
                segments={outcomeSegments}
                total={outcomeTotal}
                centerValue={pctInt(deliveryRate * 100)}
                centerLabel={t('cd_kpi_delivered')}
              />
              <DonutLegend segments={outcomeSegments} total={outcomeTotal} />
            </div>
          )}
        </Card>

        {/* Right: outcome by date (stacked bars) */}
        <Card className="p-5">
          <div className="flex items-start justify-between gap-3">
            <SectionTitle>{t('cd_rep_section_outcome_by_date')}</SectionTitle>
            <div className="flex gap-3 text-[12px] text-text-3 shrink-0">
              <LegendDot color="var(--success)" label={t('cd_legend_delivered')} />
              <LegendDot color="var(--info)" label={t('cd_rep_legend_pending')} />
              <LegendDot color="var(--danger)" label={t('cd_legend_failed')} />
            </div>
          </div>
          {outcomeSeries.isLoading ? (
            <Skeleton className="h-[240px] w-full" />
          ) : (outcomeSeries.data?.items.length ?? 0) === 0 ? (
            <div className="grid place-items-center h-[240px] text-[13px] text-text-3">
              {t('cd_no_throughput')}
            </div>
          ) : (
            <OutcomeByDateChart
              points={outcomeSeries.data?.items ?? []}
              lang={lang}
              bucketSecs={outcomeBucketSecs}
              labels={{
                delivered: t('cd_legend_delivered'),
                pending: t('cd_rep_legend_pending'),
                failed: t('cd_legend_failed'),
              }}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden="true" className="w-2 h-2 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}

function ThroughputChart({
  points,
  lang,
}: {
  points: ThroughputPoint[];
  lang: string;
}): JSX.Element {
  const data = points.map((p) => ({
    bucket: p.bucket,
    label: new Date(p.bucket).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
    sent: p.sent,
    delivered: p.delivered,
    failed: p.failed,
  }));
  return (
    <div className="h-[240px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: 'var(--text-3)', fontSize: 11 }}
            axisLine={{ stroke: 'var(--line)' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={28}
          />
          <YAxis
            tick={{ fill: 'var(--text-3)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={40}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontSize: 12,
            }}
            labelFormatter={(_, payload) => {
              const b = payload?.[0]?.payload?.bucket as string | undefined;
              return b ? new Date(b).toLocaleString(lang) : '';
            }}
          />
          <Line type="monotone" dataKey="sent" stroke="var(--volt-500)" strokeWidth={2} dot={false} />
          <Line
            type="monotone"
            dataKey="delivered"
            stroke="var(--success)"
            strokeWidth={1.6}
            dot={false}
          />
          <Line type="monotone" dataKey="failed" stroke="var(--danger)" strokeWidth={1.4} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * OutcomeByDateChart — a stacked bar chart of message outcomes per time bucket.
 * Bar height is the total volume in that bucket; the color split is the mix
 * (delivered / pending / failed), sharing the semantic colors of the outcome
 * donut. `pending` is derived per bucket as sent − delivered − failed
 * (clamped to >= 0), the same way the status band derives it globally.
 *
 * `bucketSecs` only affects the x-axis label granularity: hourly buckets show
 * time + short date, daily buckets show the date alone.
 */
function OutcomeByDateChart({
  points,
  lang,
  bucketSecs,
  labels,
}: {
  points: ThroughputPoint[];
  lang: string;
  bucketSecs: number;
  labels: { delivered: string; pending: string; failed: string };
}): JSX.Element {
  const hourly = bucketSecs < 86400;
  const data = points.map((p) => {
    const d = new Date(p.bucket);
    const label = hourly
      ? `${d.toLocaleTimeString(lang, { hour: '2-digit' })} ${d.toLocaleDateString(lang, {
          month: 'short',
          day: 'numeric',
        })}`
      : d.toLocaleDateString(lang, { month: 'short', day: 'numeric' });
    return {
      bucket: p.bucket,
      label,
      delivered: p.delivered,
      pending: Math.max(p.sent - p.delivered - p.failed, 0),
      failed: p.failed,
    };
  });
  return (
    <div className="h-[240px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: 'var(--text-3)', fontSize: 11 }}
            axisLine={{ stroke: 'var(--line)' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={28}
          />
          <YAxis
            tick={{ fill: 'var(--text-3)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={40}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: 'var(--surface-2)' }}
            contentStyle={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontSize: 12,
            }}
            labelFormatter={(_, payload) => {
              const b = payload?.[0]?.payload?.bucket as string | undefined;
              return b ? new Date(b).toLocaleString(lang) : '';
            }}
          />
          <Bar dataKey="delivered" name={labels.delivered} stackId="x" fill="var(--success)" />
          <Bar dataKey="pending" name={labels.pending} stackId="x" fill="var(--info)" />
          <Bar dataKey="failed" name={labels.failed} stackId="x" fill="var(--danger)" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface MessagesTableProps {
  messages: CampaignMessage[];
  loading: boolean;
  lang: string;
}

const MSG_STATUS_VARIANT: Record<string, 'success' | 'danger' | 'info' | 'volt' | 'neutral'> = {
  delivered: 'success',
  failed: 'danger',
  rejected: 'danger',
  sent: 'info',
  queued: 'volt',
  pending: 'volt',
};

function MessagesTable({ messages, loading, lang }: MessagesTableProps): JSX.Element {
  const { t } = useTranslation('default');
  if (loading) {
    return (
      <div className="p-5 space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    );
  }
  if (messages.length === 0) {
    return <div className="p-8 text-center text-[13px] text-text-3">{t('cd_messages_empty')}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
            <th className="px-5 py-2.5 font-medium">{t('cd_msg_col_phone')}</th>
            <th className="px-5 py-2.5 font-medium">{t('cd_msg_col_status')}</th>
            <th className="px-5 py-2.5 font-medium text-right">{t('cd_msg_col_segments')}</th>
            <th className="px-5 py-2.5 font-medium text-right">{t('cd_msg_col_cost')}</th>
            <th className="px-5 py-2.5 font-medium">{t('cd_msg_col_error')}</th>
            <th className="px-5 py-2.5 font-medium">{t('cd_msg_col_when')}</th>
          </tr>
        </thead>
        <tbody>
          {messages.map((m) => {
            const displayStatus = messageDisplayStatus(m.status, m.provider_supports_dlr);
            const variant = MSG_STATUS_VARIANT[displayStatus] ?? 'neutral';
            const when = m.delivered_at ?? m.sent_at ?? m.created_at;
            return (
              <tr key={m.id} className="border-b border-line last:border-0 hover:bg-surface-2">
                <td className="px-5 py-3 mono text-[12px] text-text">{m.phone_e164}</td>
                <td className="px-5 py-3">
                  <Badge variant={variant} className="text-[11px]">
                    {displayStatus}
                  </Badge>
                </td>
                <td className="px-5 py-3 num text-right text-text-2">{m.segments}</td>
                <td className="px-5 py-3 num text-right text-text-2">
                  {m.cost > 0 ? formatCurrency(m.cost, lang) : '—'}
                </td>
                <td className="px-5 py-3 text-text-3 text-[12px] truncate max-w-[260px]">
                  {m.error_code ? (
                    <span title={m.error_message ?? ''}>
                      <code className="mono text-[11px] text-danger">{m.error_code}</code>
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-5 py-3 text-text-3 text-[12px] whitespace-nowrap">
                  <span title={new Date(when).toLocaleString(lang)}>
                    {formatRelativeTime(when, lang)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ErrorRow({ e, lang }: { e: TopError; lang: string }): JSX.Element {
  return (
    <li className="px-5 py-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <code className="mono text-[12px] text-danger font-medium">{e.error_code}</code>
        <span className="num text-[13px] text-text-2">
          {e.count.toLocaleString(lang)}
        </span>
      </div>
      {e.sample_message && (
        <p className="text-[12px] text-text-3 mt-1 truncate" title={e.sample_message}>
          {e.sample_message}
        </p>
      )}
    </li>
  );
}
